import React from "react";
import { ImageTextCard } from "../take_training_employee/swipe_deck_player/cards/ImageTextCard";
import { VideoCard } from "../take_training_employee/swipe_deck_player/cards/VideoCard";
import { VideoEmbedCard } from "../take_training_employee/swipe_deck_player/cards/VideoEmbedCard";
import { PollCard } from "../take_training_employee/swipe_deck_player/cards/PollCard";
import { TriviaCard } from "../take_training_employee/swipe_deck_player/cards/TriviaCard";

// Shared WYSIWYG swipe-card renderer. Renders the ACTUAL player card
// component at fixed player-shell dimensions and transform-scales it to
// fill its container, so a tile looks pixel-for-pixel like the live
// SwipeDeckPlayer (after scaling). Used by both the swipe deck editor's
// storyboard tiles (SwipeDeckInputsModal) and the AI studio's embedded-deck
// preview strip (TrackGenerationStudio) so there is a single source of
// truth for how a card looks — no second, drifting "mini tile" design.
//
// Mirror the SwipeDeckPlayer shell's own sizing math so the tile renders
// the player card at the exact dimensions the Preview button would on this
// screen. Player shell:
//
//   h-[90vh] max-h-[900px] aspect-[9/16]  (desktop)
//   sm:h-full sm:w-full sm:aspect-auto    (mobile, edge-to-edge)
//
// These tiles only ever render on desktop (the studio + deck editor are
// admin-only, desktop-first), so we use the desktop branch — height capped
// at 90vh / 900px, width derived from the 9:16 aspect.
export function usePlayerReferenceDimensions() {
  const compute = () => {
    if (typeof window === "undefined") {
      // SSR-safe default — matches the typical tall-desktop render.
      return { width: 506.25, height: 900 };
    }
    const phoneHeight = Math.min(window.innerHeight * 0.9, 900);
    return { width: (phoneHeight * 9) / 16, height: phoneHeight };
  };
  const [dims, setDims] = React.useState(compute);
  React.useEffect(() => {
    const handler = () => setDims(compute());
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);
  return dims;
}

// Card-spec shape ({content_json, media_url, thumbnail_url}) → player shape
// ({content, media: {url}, thumbnail: {url}}). The player only reads a small
// fixed set of fields, so we just rewrite the keys it looks at and pass
// everything else through. Works for both the editor's cards and the AI
// studio's generated payload cards (both carry content_json + media_url).
export function editorCardToPlayerCard(card) {
  // A bound video clip with no resolved URL yet is still being cut/optimized
  // on the worker (freshly split segments arrive PENDING and the deck poll
  // fills media_url once READY). Surface that as a processing media object so
  // the card shows a "Processing video…" state instead of "Video unavailable".
  let media = null;
  if (card.media_url) {
    media = { url: card.media_url };
  } else if (card.card_type === "video" && card.media_file_uuid) {
    media = { url: null, video_status: "processing" };
  }
  return {
    ...card,
    content: card.content_json || card.content || {},
    media,
    thumbnail: card.thumbnail_url ? { url: card.thumbnail_url } : null,
  };
}

// Renders the actual player card component at fixed pixel dimensions, then
// transform-scales it to fill the tile. ResizeObserver keeps the scale in
// sync with the tile's measured width (which can change as side panels open
// / close, or the grid reflows).
//
// The inner subtree is made non-interactive (`inert`) so taps reach the
// surrounding tile and keyboard users can't tab into nested player controls
// (mute toggle, poll choices, trivia answers). `isActive={false}` keeps all
// player-side timers (auto-advance, video play) and side effects (vibration)
// dormant.
export function ScaledCardPreview({ card }) {
  const containerRef = React.useRef(null);
  const innerRef = React.useRef(null);
  const [tileWidth, setTileWidth] = React.useState(0);
  const playerDims = usePlayerReferenceDimensions();

  React.useEffect(() => {
    if (!containerRef.current) return undefined;
    const ro = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) return;
      setTileWidth(entry.contentRect.width);
    });
    ro.observe(containerRef.current);
    return () => ro.disconnect();
  }, []);

  // `inert` removes the subtree from the tab order, the a11y tree, and
  // pointer event dispatch in one shot. Set via ref because React 18
  // doesn't recognize `inert` as a JSX prop.
  React.useEffect(() => {
    if (innerRef.current) innerRef.current.inert = true;
  }, []);

  const playerCard = React.useMemo(() => editorCardToPlayerCard(card), [card]);

  const renderPlayer = () => {
    switch (card.card_type) {
      case "image_text":
        return <ImageTextCard card={playerCard} isActive={false} />;
      case "video":
        // A video card is either an uploaded clip or a YouTube/Vimeo embed —
        // route to the matching player component so an embed tile shows the
        // real player, not VideoCard's "Video unavailable" (it has no media).
        if (
          playerCard.content?.embed_provider &&
          playerCard.content?.embed_video_id
        ) {
          return (
            <VideoEmbedCard
              card={playerCard}
              isActive={false}
              interactive={false}
            />
          );
        }
        return <VideoCard card={playerCard} isActive={false} />;
      case "poll":
        return (
          <PollCard card={playerCard} isActive={false} interactive={false} />
        );
      case "quiz":
        return (
          <TriviaCard card={playerCard} isActive={false} interactive={false} />
        );
      default:
        return null;
    }
  };

  const scale = tileWidth > 0 ? tileWidth / playerDims.width : 0;

  return (
    <div ref={containerRef} className="absolute inset-0 overflow-hidden">
      {scale > 0 && (
        <div
          ref={innerRef}
          aria-hidden="true"
          style={{
            width: playerDims.width,
            height: playerDims.height,
            transform: `scale(${scale})`,
            transformOrigin: "top left",
          }}
        >
          {renderPlayer()}
        </div>
      )}
    </div>
  );
}
